import React, { Suspense } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';

// Loading component for lazy routes
export const RouteLoadingFallback = () => (
  <div className="p-8 max-w-7xl mx-auto">
    <div className="space-y-6">
      {/* Header skeleton */}
      <div className="space-y-2">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
      
      {/* Content skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Card key={i} className="p-6">
            <div className="space-y-3">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
              <div className="flex gap-2">
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-8 w-20" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  </div>
);

// Higher-order component to wrap lazy routes with suspense
export const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType<any>>) => {
  return (props: any) => (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Component {...props} />
    </Suspense>
  );
};

// Lazy route imports with proper chunking
export const LazyDashboard = React.lazy(() => import('@/pages/Dashboard'));
export const LazyOrganizations = React.lazy(() => import('@/pages/Organizations'));
export const LazyOrganizationDetails = React.lazy(() => import('@/pages/OrganizationDetails'));
export const LazyContacts = React.lazy(() => import('@/pages/Contacts'));
export const LazyContactDetails = React.lazy(() => import('@/pages/ContactDetails'));
export const LazyOpportunities = React.lazy(() => import('@/pages/Opportunities'));
export const LazyOpportunityDetails = React.lazy(() => import('@/pages/OpportunityDetails'));
export const LazyProjects = React.lazy(() => import('@/pages/Projects'));
export const LazyProjectDetails = React.lazy(() => import('@/pages/ProjectDetails'));

// Analytics and Reports (heavy components)
export const LazyAnalytics = React.lazy(() => 
  import('@/pages/Analytics').then(module => ({ default: module.default }))
);

// Communication features
export const LazyCommunications = React.lazy(() => import('@/pages/Communications'));
export const LazyEmailTemplates = React.lazy(() => import('@/pages/EmailTemplates'));
export const LazyCampaignAnalytics = React.lazy(() => import('@/pages/CampaignAnalytics'));

// Document management
export const LazyDocuments = React.lazy(() => import('@/pages/Documents'));
export const LazyDocumentDetails = React.lazy(() => import('@/pages/DocumentDetails'));

// Financial features
export const LazyInvoices = React.lazy(() => import('@/pages/Invoices'));
export const LazyInvoiceDetails = React.lazy(() => import('@/pages/InvoiceDetails'));

// Advanced features
export const LazyLandExpand = React.lazy(() => import('@/pages/LandExpand'));
export const LazyAutomation = React.lazy(() => import('@/pages/Automation'));
export const LazyRelationshipVisualizer = React.lazy(() => import('@/pages/RelationshipVisualizer'));

// Integration features
export const LazyHubSpotIntegration = React.lazy(() => import('@/pages/HubSpotIntegration'));
export const LazyDataImport = React.lazy(() => import('@/pages/DataImport'));
export const LazyDataExport = React.lazy(() => import('@/pages/DataExport'));

// Admin features
export const LazyUserManagement = React.lazy(() => import('@/pages/UserManagement'));

// Contract and legal
export const LazyContractGenerator = React.lazy(() => import('@/pages/ContractGenerator'));

// Create routes
export const LazyCreateContact = React.lazy(() => import('@/pages/CreateContact'));
export const LazyCreateOrganization = React.lazy(() => import('@/pages/CreateOrganization'));
export const LazyCreateOpportunity = React.lazy(() => import('@/pages/CreateOpportunity'));
export const LazyCreateProject = React.lazy(() => import('@/pages/CreateProject'));
export const LazyCreateTask = React.lazy(() => import('@/pages/CreateTask'));
export const LazyCreateActivity = React.lazy(() => import('@/pages/CreateActivity'));
export const LazyCreateGift = React.lazy(() => import('@/pages/CreateGift'));
export const LazyCreateInvoice = React.lazy(() => import('@/pages/CreateInvoice'));

// Edit routes
export const LazyEditContact = React.lazy(() => import('@/pages/EditContact'));
export const LazyEditOrganization = React.lazy(() => import('@/pages/EditOrganization'));
export const LazyEditOpportunity = React.lazy(() => import('@/pages/EditOpportunity'));
export const LazyEditTask = React.lazy(() => import('@/pages/EditTask'));
export const LazyEditActivity = React.lazy(() => import('@/pages/EditActivity'));
export const LazyEditGift = React.lazy(() => import('@/pages/EditGift'));

// Preload critical routes
export const preloadCriticalRoutes = () => {
  // Preload dashboard and organizations as they're most commonly accessed
  import('@/pages/Dashboard');
  import('@/pages/Organizations');
  import('@/pages/Contacts');
};

// Preload routes based on user navigation patterns
export const preloadRouteOnHover = (routeName: string) => {
  switch (routeName) {
    case 'dashboard':
      import('@/pages/Dashboard');
      break;
    case 'organizations':
      import('@/pages/Organizations');
      break;
    case 'contacts':
      import('@/pages/Contacts');
      break;
    case 'opportunities':
      import('@/pages/Opportunities');
      break;
    case 'projects':
      import('@/pages/Projects');
      break;
    case 'analytics':
      import('@/pages/Analytics');
      break;
    case 'automation':
      import('@/pages/Automation');
      break;
    default:
      break;
  }
};

// Route chunks configuration for Vite
export const routeChunks = {
  // Core routes - loaded immediately
  core: ['Dashboard', 'Organizations', 'Contacts'],
  
  // Business logic routes - medium priority
  business: ['Opportunities', 'Projects', 'Tasks', 'Activities'],
  
  // Analytics and reporting - heavy components
  analytics: ['Analytics', 'CampaignAnalytics', 'RelationshipVisualizer'],
  
  // Administrative features - low priority
  admin: ['UserManagement', 'Automation', 'DataImport', 'DataExport'],
  
  // Integration features - low priority
  integrations: ['HubSpotIntegration', 'ContractGenerator'],
  
  // Financial features - medium priority
  financial: ['Invoices', 'InvoiceDetails'],
  
  // Document management - medium priority
  documents: ['Documents', 'DocumentDetails'],
  
  // Communication features - medium priority
  communication: ['Communications', 'EmailTemplates']
};